import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { TailwindModal } from "../../../../components/TailwindModal";
import {
  FACET_AUTOGENERATE_CONFIRM_DESC,
  FACET_AUTOGENERATE_CONFIRM_KEYS_HINT,
  FACET_AUTOGENERATE_CONFIRM_LABEL,
  FACET_AUTOGENERATE_CONFIRM_TITLE,
} from "../../tars-constants";
import FacetAutogenerateKeyList from "./FacetAutogenerateKeyList";
import { useFacetAutogenerateSourceKeys } from "./useFacetAutogenerateSourceKeys";

interface FacetAutogenerateConfirmModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: (matrixKeys: string[]) => void;
}

function FacetAutogenerateConfirmModalInner({
  isOpen,
  onCancel,
  onConfirm,
}: FacetAutogenerateConfirmModalProps) {
  const { keys, loading, fetchError, needsSourceSelection } =
    useFacetAutogenerateSourceKeys(isOpen);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isOpen) setSelected(new Set(keys));
  }, [isOpen, keys]);

  const toggleKey = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) => (prev.size === keys.length ? new Set() : new Set(keys)));
  };

  const toggleFiltered = (filteredKeys: string[]) => {
    setSelected((prev) => {
      const allFilteredSelected =
        filteredKeys.length > 0 && filteredKeys.every((key) => prev.has(key));
      const next = new Set(prev);
      if (allFilteredSelected) filteredKeys.forEach((key) => next.delete(key));
      else filteredKeys.forEach((key) => next.add(key));
      return next;
    });
  };

  const canConfirm =
    !needsSourceSelection &&
    selected.size > 0 &&
    !loading &&
    !fetchError;

  return (
    <TailwindModal isOpen={isOpen} onClose={onCancel}>
      <div className="mx-auto w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
        <h3 className="text-lg font-medium text-gray-900">{FACET_AUTOGENERATE_CONFIRM_TITLE}</h3>
        <p className="mt-2 text-sm text-gray-500">{FACET_AUTOGENERATE_CONFIRM_DESC}</p>
        <p className="mt-1 text-sm font-bold text-gray-700 ">{FACET_AUTOGENERATE_CONFIRM_KEYS_HINT}</p>
        <FacetAutogenerateKeyList
          keys={keys}
          loading={loading}
          fetchError={fetchError}
          needsSourceSelection={needsSourceSelection}
          selected={selected}
          onToggle={toggleKey}
          onToggleAll={toggleAll}
          onToggleFiltered={toggleFiltered}
        />
        <div className="mt-5 flex flex-row-reverse gap-3">
          <button
            type="button"
            disabled={!canConfirm}
            className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => onConfirm([...selected])}
          >
            {FACET_AUTOGENERATE_CONFIRM_LABEL}
          </button>
          <button
            type="button"
            className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            onClick={onCancel}
          >
            Cancel
          </button>
        </div>
      </div>
    </TailwindModal>
  );
}

export default observer(FacetAutogenerateConfirmModalInner);
